/**
 * useRailWidth — persisted pixel width for the full-page record's right-hand
 * details rail, clamped to [280, 50% viewport] with a 340px default. Wraps
 * the shared `useResizablePanel` primitive for keyboard/drag resize and
 * persists to localStorage so the user's choice survives refresh. Persistence
 * is desktop-only (`isDesktop`) — the rail has no width control below `lg`, so
 * writing a mobile-clamped mount value would overwrite the desktop choice. When
 * the viewport crosses from below `lg` into desktop, the stored desktop width is
 * restored before persistence resumes, so a mobile mount never clobbers it.
 * Width persistence is orthogonal to the collapsed state (see `useRailCollapsed`).
 */

import { useCallback, useEffect, useState } from 'react';

import { useResizablePanel } from '@/hooks/useResizablePanel';

export const RAIL_DEFAULT_WIDTH = 340;
export const RAIL_MIN_WIDTH = 280;
export const RAIL_MAX_WIDTH_PERCENT = 0.5;

const WIDTH_KEY = 'crm-record-rail-width';

/** Max rail width in pixels: half the viewport, floored, but never below RAIL_MIN_WIDTH. */
export function computeRailMaxWidth(): number {
  return Math.max(
    Math.floor(window.innerWidth * RAIL_MAX_WIDTH_PERCENT),
    RAIL_MIN_WIDTH
  );
}

/**
 * Clamped read of the persisted rail width, exported so the loading skeleton
 * can render at the same width the mounted rail will initialize to — the
 * skeleton and useRailWidth's own initializer must never disagree.
 */
export function getStoredRailWidth(): number {
  try {
    const raw = localStorage.getItem(WIDTH_KEY);
    // Treat missing and blank/whitespace-only values alike: Number('') and
    // Number('   ') are 0 (finite), which would slip past the check below and
    // clamp to RAIL_MIN_WIDTH instead of the intended 340 default.
    if (raw === null || raw.trim() === '') {
      return RAIL_DEFAULT_WIDTH;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) {
      return RAIL_DEFAULT_WIDTH;
    }
    // Out-of-range finite values CLAMP to the nearest bound — they do NOT reset
    // to RAIL_DEFAULT_WIDTH. This is deliberate (issue #13786 Definition of Done:
    // "when a stored value is out of range for the current viewport it clamps to
    // bounds on read") and pinned by the parity table in this hook's tests
    // ('50' -> RAIL_MIN_WIDTH, '99999' -> viewport max). A stored value above the
    // max means the viewport shrank; clamping to the new max preserves the user's
    // "as wide as possible" intent, whereas snapping back to 340 discards a width
    // they never un-chose. Do not "fix" this to return the default.
    return Math.min(Math.max(parsed, RAIL_MIN_WIDTH), computeRailMaxWidth());
  } catch {
    return RAIL_DEFAULT_WIDTH;
  }
}

export interface RailWidthState {
  /** Current rail width in pixels. */
  width: number;
  /** Whether the rail is currently being drag-resized. */
  isResizing: boolean;
  /** Start a drag-resize from a mousedown on the resize handle. */
  handleResizeStart: (e: React.MouseEvent) => void;
  /** Adjust the width by a signed delta (positive grows), clamped to bounds. */
  resizeBy: (delta: number) => void;
  /** Reset the rail to its default width. */
  resetWidth: () => void;
}

export function useRailWidth(isDesktop: boolean): RailWidthState {
  const [initialWidth] = useState(getStoredRailWidth);

  const { width, isResizing, handleResizeStart, resizeBy } = useResizablePanel({
    initialWidth,
    minWidth: RAIL_MIN_WIDTH,
    maxWidthPercent: RAIL_MAX_WIDTH_PERCENT,
    edge: 'right',
  });

  // Restore the stored desktop width when the viewport crosses into `lg`.
  // If the page mounted below `lg`, `initialWidth` was clamped to the narrow
  // viewport (down to RAIL_MIN_WIDTH), so the panel holds a mobile value while
  // localStorage still holds the untouched desktop choice (we never persist on
  // mobile). Without this, the desktop-persist effect below would write the
  // stale mobile width back over the saved desktop width. Re-read on the
  // false→true transition and correct the panel before persistence runs.
  // Previous-prop compare in render (not an effect) so the corrected width is
  // committed in the same paint — no mobile-width flash, and the persist effect
  // never observes the stale value. resizeBy's internal setWidth(prev => …)
  // sees prev === width, so the delta resolves to the (re-clamped) stored value.
  const [prevIsDesktop, setPrevIsDesktop] = useState(isDesktop);
  if (isDesktop !== prevIsDesktop) {
    setPrevIsDesktop(isDesktop);
    if (isDesktop) {
      const storedDesktopWidth = getStoredRailWidth();
      if (storedDesktopWidth !== width) {
        resizeBy(storedDesktopWidth - width);
      }
    }
  }

  useEffect(() => {
    // Persist only on desktop. Below `lg` the rail renders as a full-width
    // stacked section with no inline width or handle, and readStoredWidth
    // clamps to the narrow viewport (down to RAIL_MIN_WIDTH) — writing that
    // mount value back would clobber the user's chosen desktop width.
    if (isResizing || !isDesktop) {
      return;
    }
    try {
      localStorage.setItem(WIDTH_KEY, String(width));
    } catch {
      // ignore persistence failures (private mode, quota, etc.)
    }
  }, [width, isResizing, isDesktop]);

  // Reset persists the default on purpose. resizeBy moves width to 340, the
  // persist effect above then writes 340, and reload shows 340 — standard
  // "reset to default, and it sticks" semantics. It deliberately overwrites
  // the stored custom width: discarding that customization is what reset is
  // for. A transient reset (340 this session, old width back on reload) would
  // be surprising and is not the contract — do not "fix" this to skip
  // persistence on reset.
  const resetWidth = useCallback(
    () => resizeBy(RAIL_DEFAULT_WIDTH - width),
    [resizeBy, width]
  );

  return { width, isResizing, handleResizeStart, resizeBy, resetWidth };
}
